import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { ArrowLeft } from 'react-feather';
import { TagCategory } from '@trinity/chat';
import { PatientJourneyDimensionType } from '@trinity/chat/enums/conference';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import {
  TagDetailField,
  TagDetailsColorSelect,
  TagDetailsBannerImage,
  TagDetailsSectionsContainer,
  TagDetailsInfoSection,
  TagDetailsPatientJourneySection,
  TagDetailsCustomSummarySection,
  TagDetailsExtractionSection,
} from '@trinity/chat-components/components/ProjectTags/TagDetails';
import { ExtractionQuestionsForm } from '@trinity/chat-components/components/ExtractionQuestions/index.js';
import { useUpdateProjectTagMutation } from './hooks/useUpdateProjectTagMutation';
import { useUpdateTagPromptsMutation } from './hooks/useUpdateTagPromptsMutation';
import styles from './style/Tag.Details.module.css';

type Props = {
  tag: ProjectConferenceTagWithMetadata;
};

export const TagDetails = ({ tag }: Props) => {
  const params = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = Number(params.projectId);

  const handleBackToTable = useCallback(() => {
    navigate(`/projects/${params.projectId}/tags`);
  }, [navigate, params.projectId]);

  const colorMutation = useUpdateProjectTagMutation(projectId);
  const promptsMutation = useUpdateTagPromptsMutation(projectId);

  const handleColorChange = useCallback(
    (color: string) => {
      colorMutation.mutate({
        color,
        hidden: tag.hidden,
        includeSummary: tag.includeSummary,
        parentId: tag.parent?.id ?? null,
        tagId: tag.id,
        title: tag.base.name,
      });
    },
    [colorMutation, tag]
  );

  const handleSavePrompts = useCallback(
    async (prompts: string[]) => {
      await promptsMutation.mutateAsync({ tagId: tag.id, summary: prompts });
    },
    [promptsMutation, tag.id]
  );

  const handleSaveExtraction = useCallback(
    async (items: { name: string; description: string; ordinal: number }[]) => {
      await promptsMutation.mutateAsync({ tagId: tag.id, extraction: items });
    },
    [promptsMutation, tag.id]
  );

  const handleSaveExtractionQuestions = useCallback(async () => {
    // TODO: implement persistence
  }, []);

  const bannerImageUrl = tag.base.locationDescriptor?.dashboardBannerImageUrl;
  const isPatientJourney = tag.base.tagCategory === TagCategory.PatientJourney;
  const formattedAddedOn = tag.addedOn ? new Date(tag.addedOn).toLocaleDateString() : 'N/A';
  const formattedLastModified = tag.base.lastModified
    ? new Date(tag.base.lastModified).toLocaleDateString()
    : 'N/A';

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.main}>
          <BackButton onClick={handleBackToTable}>
            <ArrowLeft size={16} />
            Back to Table
          </BackButton>

          <TagDetailsSectionsContainer>
            <TagDetailsInfoSection
              title={tag.base.name}
              tagCategory={tag.base.tagCategory}
              datePublished={formattedAddedOn}
              parentTag={tag.parent?.name ?? (tag.parent?.id != null ? `ID: ${tag.parent.id}` : 'None')}
              coverageCount={tag.metadata.taggedMomentsCount}
              statusNode={
                <TagDetailField label="Published Status" value={tag.hidden ? 'Hidden' : 'Published'} />
              }
              colorNode={
                <TagDetailsColorSelect
                  color={tag.base.color}
                  onChange={handleColorChange}
                  isPending={colorMutation.isPending}
                />
              }
              lastModified={formattedLastModified}
              includeInSummary={tag.includeSummary ? 'Yes' : 'No'}
            />

            {isPatientJourney && (
              <TagDetailsPatientJourneySection
                order={tag.ordinal?.toString() ?? 'N/A'}
                dashboardPublishType={tag.base.dashboardPublishType}
                patientJourneyIcon={tag.base.patientJourneyIcon}
                visibility={tag.base.visibility}
                dashboardDimensionType={
                  tag.base.dashboardDimensionType as PatientJourneyDimensionType | null | undefined
                }
                bannerImageNode={<TagDetailsBannerImage value={bannerImageUrl} />}
              />
            )}

            <TagDetailsCustomSummarySection
              prompts={tag.base.customSummaryPrompt}
              onSave={handleSavePrompts}
              isSaving={promptsMutation.isPending}
            />

            <TagDetailsExtractionSection
              items={tag.base.extractionPrompts ?? []}
              onSave={handleSaveExtraction}
              isSaving={promptsMutation.isPending}
            />

            <ExtractionQuestionsForm items={[]} onSave={handleSaveExtractionQuestions} />
          </TagDetailsSectionsContainer>
        </div>
      </div>
    </div>
  );
};

TagDetails.displayName = 'Project.Tags.Tag.Details';

const BackButton = styled.button`
  color: ${(props) => props.theme?.palette?.wl?.primary?.main || 'var(--pri-02)'};
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-semibold);
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 20px;
`;
